import type { Broker } from '@/services';
import { queryClient } from '@/app/context/query-client';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { BrokerContextType } from './types';
import { useAuth } from '../auth/auth.provider';

const STORAGE_KEY = 'digit-alba:broker-context';

const BrokerContext = createContext<BrokerContextType | undefined>(undefined);

type BrokerProviderProps = {
  children: ReactNode;
};

function safeParseBroker(raw: string | null): Broker | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed as Broker;
  } catch {
    return null;
  }
}

export function BrokerProvider({ children }: BrokerProviderProps) {
  const { user, loading } = useAuth();
  const [selectedBroker, setSelectedBrokerState] = useState<Broker | null>(() =>
    safeParseBroker(localStorage.getItem(STORAGE_KEY))
  );

  const setSelectedBroker = useCallback((broker: Broker | null) => {
    setSelectedBrokerState(broker);
  }, []);

  const clearSelectedBroker = useCallback(() => {
    setSelectedBrokerState(null);
  }, []);

  const wasAuthenticated = useRef(false);

  // Limpa o broker selecionado e o cache ao deslogar.
  //
  // Só limpa na TRANSIÇÃO de autenticado -> não autenticado. Tratar "não tenho
  // usuário agora" como logout apagava a seleção indevidamente: signInFirstStep
  // marca loading=false antes do MFA, com user ainda nulo, e o catch de
  // checkUserSession zera o user em qualquer erro transitório do Cognito.
  useEffect(() => {
    if (loading) return;

    if (user) {
      wasAuthenticated.current = true;
      return;
    }

    if (!wasAuthenticated.current) return;
    wasAuthenticated.current = false;

    setSelectedBrokerState(null);
    try {
      localStorage.removeItem(STORAGE_KEY);
      queryClient.removeQueries({ queryKey: ['brokers'] });
      queryClient.removeQueries({ queryKey: ['broker-products'] });
    } catch {
      // ignore
    }
  }, [loading, user]);

  useEffect(() => {
    try {
      if (selectedBroker) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(selectedBroker));
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // ignore
    }
  }, [selectedBroker]);

  const value = useMemo<BrokerContextType>(
    () => ({
      selectedBroker,
      setSelectedBroker,
      clearSelectedBroker,
    }),
    [selectedBroker, setSelectedBroker, clearSelectedBroker]
  );

  return (
    <BrokerContext.Provider value={value}>{children}</BrokerContext.Provider>
  );
}

export function useBrokerContext() {
  const context = useContext(BrokerContext);
  if (context === undefined) {
    throw new Error('useBrokerContext must be used within a BrokerProvider');
  }
  return context;
}

/**
 * Mesmo contexto, sem lançar erro se estiver fora do provider.
 * Use no shell/layout para evitar crash em HMR, error boundaries ou duplicidade de módulos.
 */
export function useBrokerContextSafe(): BrokerContextType | null {
  return useContext(BrokerContext) ?? null;
}
